import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import type { TimelineSpineSegmentStop } from './timelineSpineTypes'

type Registry = {
  registerDot: (roundId: string, el: HTMLElement | null) => void
  registerContent: (roundId: string, el: HTMLElement | null) => void
  subscribeLayout: (listener: () => void) => () => void
  roundIds: string[]
  segmentStops: TimelineSpineSegmentStop[]
  getDot: (roundId: string) => HTMLElement | undefined
  getContent: (roundId: string) => HTMLElement | undefined
}

const TimelineSpineContext = createContext<Registry | null>(null)

export function TimelineSpineProvider({
  roundIds,
  segmentStops,
  children,
}: {
  roundIds: string[]
  segmentStops: TimelineSpineSegmentStop[]
  children: ReactNode
}) {
  const dotsRef = useRef(new Map<string, HTMLElement>())
  const contentRef = useRef(new Map<string, HTMLElement>())
  const listenersRef = useRef(new Set<() => void>())

  const notifyLayout = useCallback(() => {
    listenersRef.current.forEach((listener) => listener())
  }, [])

  const registerDot = useCallback(
    (roundId: string, el: HTMLElement | null) => {
      if (el) {
        if (dotsRef.current.get(roundId) === el) return
        dotsRef.current.set(roundId, el)
      } else {
        if (!dotsRef.current.has(roundId)) return
        dotsRef.current.delete(roundId)
      }
      notifyLayout()
    },
    [notifyLayout],
  )

  const registerContent = useCallback(
    (roundId: string, el: HTMLElement | null) => {
      if (el) {
        if (contentRef.current.get(roundId) === el) return
        contentRef.current.set(roundId, el)
      } else {
        if (!contentRef.current.has(roundId)) return
        contentRef.current.delete(roundId)
      }
      notifyLayout()
    },
    [notifyLayout],
  )

  const subscribeLayout = useCallback((listener: () => void) => {
    listenersRef.current.add(listener)
    return () => {
      listenersRef.current.delete(listener)
    }
  }, [])

  const getDot = useCallback((roundId: string) => dotsRef.current.get(roundId), [])
  const getContent = useCallback((roundId: string) => contentRef.current.get(roundId), [])

  const value = useMemo<Registry>(
    () => ({
      registerDot,
      registerContent,
      subscribeLayout,
      roundIds,
      segmentStops,
      getDot,
      getContent,
    }),
    [registerDot, registerContent, subscribeLayout, roundIds, segmentStops, getDot, getContent],
  )

  return <TimelineSpineContext.Provider value={value}>{children}</TimelineSpineContext.Provider>
}

export function useTimelineSpineRegistry() {
  const ctx = useContext(TimelineSpineContext)
  if (!ctx) {
    throw new Error('useTimelineSpineRegistry must be used within TimelineSpineProvider')
  }
  return ctx
}

export function useTimelineSpineRegisterDot(roundId: string) {
  const ctx = useContext(TimelineSpineContext)
  const registerRef = useRef(ctx?.registerDot)
  registerRef.current = ctx?.registerDot

  return useCallback(
    (el: HTMLSpanElement | null) => {
      registerRef.current?.(roundId, el)
    },
    [roundId],
  )
}

export function useTimelineSpineRegisterContent(roundId: string) {
  const ctx = useContext(TimelineSpineContext)
  const registerRef = useRef(ctx?.registerContent)
  registerRef.current = ctx?.registerContent

  return useCallback(
    (el: HTMLDivElement | null) => {
      registerRef.current?.(roundId, el)
    },
    [roundId],
  )
}
